import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Skeleton, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui';
import { useCouponDefsQuery, useCouponIssueMutation } from '../hooks/useUserCouponQuery';

interface UserCouponPanelProps {
  userId: string;
}

export const UserCouponPanel = ({ userId }: UserCouponPanelProps) => {
  const { t } = useTranslation('common');
  const couponQuery = useCouponDefsQuery();
  const issueMutation = useCouponIssueMutation();
  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // [coupon-audit] 발급 가능 = FE 파생(issuable: 미만료+쿼터 잔여) — 종전 wire 에 없는
  // `active` 필드를 거르던 버그(항상 빈 목록)의 근본 수정은 useUserCouponQuery 쪽.
  const activeCoupons = (couponQuery.data ?? []).filter((c) => c.issuable);

  const handleIssue = async () => {
    if (!selectedCouponId) return;
    await issueMutation.mutateAsync({ couponId: selectedCouponId, userId });
    const issued = activeCoupons.find((c) => c.couponId === selectedCouponId);
    setSuccessMsg(t('users.coupon.issueSuccess', { name: issued?.couponName ?? '' }));
    setConfirmOpen(false);
    setSelectedCouponId(null);
  };

  if (couponQuery.isPending) {
    return (
      <div className="space-y-2" role="status" aria-live="polite">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[44px] w-full" />
        ))}
      </div>
    );
  }

  if (couponQuery.isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-2 rounded-xl py-10 text-center text-sm"
        style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
      >
        <p>{t('users.coupon.loadFailed')}</p>
        <Button variant="outline" size="sm" onClick={() => void couponQuery.refetch()}>
          {t('users.actions.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {successMsg && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
        >
          {successMsg}
        </div>
      )}

      {activeCoupons.length === 0 ? (
        <p className="py-10 text-center text-sm" style={{ color: 'var(--content-muted)' }}>
          {t('users.coupon.noCoupons')}
        </p>
      ) : (
        <div
          className="overflow-hidden rounded-xl"
          style={{ border: '1px solid var(--divider-strong)' }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--surface-1)', borderBottom: '1px solid var(--divider-strong)' }}>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('users.coupon.cols.name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('users.coupon.cols.discount')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                  {t('users.coupon.cols.validUntil')}
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {activeCoupons.map((coupon) => (
                <tr
                  key={coupon.couponId}
                  style={{ borderBottom: '1px solid var(--divider-soft)' }}
                >
                  <td className="px-4 py-3 font-medium" style={{ color: 'var(--content-strong)' }}>
                    {coupon.couponName}
                  </td>
                  <td className="px-4 py-3" style={{ color: 'var(--content-default)' }}>
                    {coupon.discountType === 'PERCENT'
                      ? `${coupon.discountValue}%`
                      : `${coupon.discountValue.toLocaleString('ko-KR')}원`}
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--content-muted)' }}>
                    {coupon.endDate ? new Date(coupon.endDate).toLocaleDateString('ko-KR') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedCouponId(coupon.couponId);
                        setConfirmOpen(true);
                        setSuccessMsg(null);
                      }}
                    >
                      {t('users.coupon.issue')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => !issueMutation.isPending && setConfirmOpen(o)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.coupon.confirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('users.coupon.confirmDesc', {
                name: activeCoupons.find((c) => c.couponId === selectedCouponId)?.couponName ?? '',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm" disabled={issueMutation.isPending}>
                {t('users.actions.cancel')}
              </Button>
            </DialogClose>
            <Button
              size="sm"
              onClick={() => void handleIssue()}
              disabled={issueMutation.isPending}
            >
              {t('users.actions.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
